import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
} from '@mui/icons-material';
import Navigation from './Navigation';
import SidebarFooter from './SidebarFooter';

const drawerWidth = 280;

interface MainLayoutProps {
  children?: React.ReactNode;
  toggleTheme?: () => void;
  isDarkMode?: boolean;
}

const MainLayout = ({ children, toggleTheme, isDarkMode = true }: MainLayoutProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Modern Header */}
      <Box sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, sm: 2 },
        minHeight: 56,
      }}>
        <Box
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: 2,
            overflow: 'hidden',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.background.default,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <img
            src="/images/logo-mark-white.svg"
            alt="SmartAI"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: 4,
            }}
          />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography 
            variant="h6"
            noWrap
            sx={{ 
              fontWeight: 600, 
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.2,
              letterSpacing: 0.5,
            }}
          >
            Creative Studio
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              lineHeight: 1,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            The Creative Assistant
          </Typography>
        </Box>
      </Box>
      
      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Navigation />
      </Box>
      
      {/* Footer */}
      <SidebarFooter toggleTheme={toggleTheme} isDarkMode={isDarkMode} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: '100%',
            ml: 0,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: theme.palette.mode === 'dark' 
              ? theme.palette.background.paper 
              : theme.palette.background.default,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2,
                width: 44,
                height: 44,
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <img
                src="/images/logo-mark-white.svg"
                alt="SmartAI"
                style={{ width: 32, height: 32, marginRight: 12, borderRadius: 100 }}
              />
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 600,
                }}
              >
                Creative Studio
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: theme.palette.background.paper,
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8, md: 0 },
          minHeight: '100vh',
        }}
      >
        {children || <Outlet />}
      </Box>
    </Box>
  );
};

export default MainLayout;